import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ProjectService } from '../../Services/project.service';
import { WorkHabitsData } from './work-habits-card.types';

// Work habits describe a slow-moving trait, so this card owns a fixed trailing window instead
// of following the profile page pickers. Shorter ranges make the medians visibly unstable.
const HABITS_WINDOW_DAYS = 30;

@Component({
  selector: 'app-work-habits-card',
  templateUrl: './work-habits-card.component.html',
  styleUrls: ['./work-habits-card.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkHabitsCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userId: number | string | null = null;

  readonly windowDays = HABITS_WINDOW_DAYS;
  loading = false;
  error = false;
  habitsData: WorkHabitsData | null = null;

  private requestSubscription?: Subscription;
  private lastRequestKey = '';

  constructor(
    private readonly projectService: ProjectService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId']) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
  }

  load(): void {
    const employeeId = Number(this.userId);
    if (!Number.isFinite(employeeId) || employeeId <= 0) {
      this.habitsData = null;
      this.loading = false;
      return;
    }

    const startDate = this.daysAgo(HABITS_WINDOW_DAYS - 1);
    const endDate = this.formatDate(new Date()) as string;
    const requestKey = `${employeeId}:${startDate}:${endDate}`;
    if (requestKey === this.lastRequestKey) return;
    this.lastRequestKey = requestKey;

    this.requestSubscription?.unsubscribe();
    this.loading = true;
    this.error = false;
    this.habitsData = null;
    this.cdr.markForCheck();

    this.requestSubscription = this.projectService.getEmployeeWorkHabits(employeeId, {
      start_date: startDate,
      end_date: endDate,
    }).subscribe({
      next: (response) => {
        this.habitsData = response?.data ?? null;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.habitsData = null;
        this.loading = false;
        this.error = true;
        this.cdr.markForCheck();
      },
    });
  }

  retry(): void {
    this.lastRequestKey = '';
    this.load();
  }

  private formatDate(value: Date | string | null): string | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private daysAgo(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return this.formatDate(date) as string;
  }
}
